import styles from './Rentals.module.css';

import classNames from 'classnames';
import React, { useRef } from 'react';

import { useCatalogRoute } from 'src/hooks/useCatalogRoute';
import { useCatalogPaging } from 'src/hooks/useCatalogPaging';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';
import { useCatalogFilterPanel } from 'src/hooks/useCatalogFilterPanel';

import { catalogHead } from 'src/utils/pageHead';
import { toRentalParams, buildCatalogPath } from 'src/utils/catalogUrl';

import { useListRentalListings } from 'src/queries';

import { Stack } from 'src/components/themed/Stack';
import { DocumentHead } from 'src/components/DocumentHead';
import { Typography } from 'src/components/themed/Typography';
import { VerticalSwitch } from 'src/components/VerticalSwitch';
import { useAssistantEntry } from 'src/components/AssistantPanel';
import { CatalogBody, CatalogPage, ListingCount, CatalogTopRow } from 'src/components/ListingGrid';

import { AvailableRentals } from './AvailableRentals';

/**
 * Rental browse (public discovery, like Listings) at `/rent/:make?/:model?`, its facets and page in
 * the address; the old `/rentals` redirects here. The renter's own rentals live under the Deals tab
 * (Deals → Rental Deals), so this tab is browse-only.
 */
export const Rentals = () => {
  const route = useCatalogRoute('rent');
  const scrollContainer = useRef<HTMLDivElement>(null);
  // One page of the catalog, asked of the api like the sale's (`limit` and `offset`).
  const query = useListRentalListings(toRentalParams(route), { enabled: route.ready && !route.unknownSlug });
  const state = useQueryScreenState(query);
  const total = query.data?.total ?? 0;
  useCatalogPaging(route, query.isPlaceholderData ? undefined : query.data?.total, scrollContainer);

  const filters = useCatalogFilterPanel('rent', route);
  // The assistant (api#381, app#397): "Ask GAC AI" beside Buy / Rent, its window on demand.
  const assistant = useAssistantEntry('rent');

  return (
    <>
      <DocumentHead
        {...catalogHead({
          vertical: 'rent',
          makeName: route.make?.name,
          modelName: route.model?.name,
          makeSlug: route.makeSlug,
          modelSlug: route.modelSlug,
          filters: route.filters,
          page: route.page,
          total: state === 'ready' && !query.isPlaceholderData ? query.data?.total : undefined,
          firstCar: query.data?.items[0],
        })}
      />
      {filters.sheet}
      {assistant.window}
      <div ref={scrollContainer} className={classNames('container', styles.container)}>
        <CatalogPage gap={4} p={4} pb={30}>
          <CatalogTopRow>
            <VerticalSwitch
              active="rent"
              buyTo={buildCatalogPath({ vertical: 'buy' })}
              rentTo={buildCatalogPath({ vertical: 'rent' })}
            />
            {assistant.pill}
          </CatalogTopRow>
          <Stack width="100%" flexDirection="row" justifyContent="space-between" alignItems="center" pl={4}>
            <Stack gap={1}>
              <Typography as="h1" fontWeight="600" variant="title3" color="white">
                Rentals
              </Typography>
              {route.ready && !route.unknownSlug && state === 'ready' && total > 0 ? (
                <ListingCount total={total} />
              ) : null}
            </Stack>
            {/* The funnel opens the sheet — a phone's filters. A laptop has them beside the grid. */}
            {filters.funnel}
          </Stack>
          <CatalogBody>
            {filters.sidebar}
            <AvailableRentals
              route={route}
              query={query}
              // A retry brings the lists of the filter back with the cars.
              onRetry={() => {
                query.refetch();
                filters.refresh();
              }}
              onRetryLookup={filters.retryLookup}
            />
          </CatalogBody>
        </CatalogPage>
      </div>
    </>
  );
};
